import { handleActions } from 'redux-actions'
import isEmpty from 'lodash/isEmpty'

import createActionFactory from 'utils/createActionFactory'
import { fetchVenueMenu, fetchVenueDiscount } from 'api/venues'
import get from 'lodash/get'

/**
 * Actions
 */

const ca = createActionFactory(`unicorn/venue/menu/`)
const _fetchMenu = ca.promise('FETCH_MENU')
const _fetchDiscount = ca.promise('FETCH_DISCOUNT')

// Once a discounts fetch has failed (e.g. the endpoint is returning 5xx),
// wait this long before allowing another attempt for the same venue. Without
// this, every page mount that calls loadDiscounts (vendor page, each step of
// checkout) re-hits the failing endpoint immediately.
const DISCOUNT_RETRY_COOLDOWN_MS = 30 * 1000

export const loadMenu = (id, params) => (dispatch, getState) => {
  // Guard against loading before the venue id is available (e.g. route params
  // not yet resolved / page not fully loaded). Without this we fire a malformed
  // request to /venues/undefined/menus.
  if (!id) return Promise.resolve()

  const isMenuLoaded = String(id) === String(selectMenuId(getState()))

  if (isMenuLoaded) return Promise.resolve()

  dispatch(_fetchMenu(id))

  return fetchVenueMenu(id, params)
    .then(menu => dispatch(_fetchMenu.fulfilled({ payload: { menu, id } })))
    .catch(error => dispatch(_fetchMenu.rejected({ payload: error })))
}

export const loadDiscounts = (venueId) => (dispatch, getState) => {
  if (!venueId) return

  const status = get(getState(), `vendor.menu.discountsStatus[${venueId}]`)

  if (status) {
    const isInFlight = status.state === 'loading'
    const isCoolingDown =
      status.state === 'error' &&
      Date.now() - status.erroredAt < DISCOUNT_RETRY_COOLDOWN_MS

    if (isInFlight || isCoolingDown) {
      return Promise.resolve()
    }
  }

  dispatch(_fetchDiscount({ venueId }))

  try {
    return fetchVenueDiscount(venueId)
      .then(discount => dispatch(_fetchDiscount.fulfilled({ payload: { discount, venueId } })))
      .catch(error => dispatch(_fetchDiscount.rejected({ payload: { error, venueId } })))
  } catch (error) {
    dispatch(_fetchDiscount.fulfilled({ payload: { discount:[], venueId } }))
  }
}

/**
 * Reducer
 */

const initialState = {
  isLoading: false,
  id: null,
  menus: [],
  discounts: {},
  discountsStatus: {}
}

export default handleActions(
  {
    [_fetchMenu]: state => ({
      ...state,
      isLoading: true,
      id: null,
      menus: [],
    }),
    [_fetchMenu.fulfilled]: (state, { payload: { id, menu } }) => ({
      ...state,
      id,
      menus: menu,
      isLoading: false,
    }),
    [_fetchMenu.rejected]: state => ({
      ...state,
      isLoading: false,
    }),
    [_fetchDiscount]: (state, { payload: { venueId } }) => ({
      ...state,
      discountsStatus: {
        ...state.discountsStatus,
        [venueId]: { state: 'loading' }
      }
    }),
    [_fetchDiscount.fulfilled]: (state, { payload: { discount, venueId } }) => ({
      ...state,
      discounts: {
        [venueId]: discount
      },
      discountsStatus: {
        ...state.discountsStatus,
        [venueId]: { state: 'loaded' }
      }
    }),
    [_fetchDiscount.rejected]: (state, { payload: { venueId } }) => ({
      ...state,
      discountsStatus: {
        ...state.discountsStatus,
        [venueId]: { state: 'error', erroredAt: Date.now() }
      }
    })
  },
  initialState,
)

/**
 * Helpers
 */

export const formatMenu = menu => {
  if (!menu || !menu.optionGroups || isEmpty(menu.optionGroups)) {
    return []
  }

  const { normal = [], advanced = [] } = menu.optionGroups
  const optionGroups = [...normal, ...advanced]

  return optionGroups
}

/**
 * Selectors
 */

export const selectMenuId = state => state.vendor.menu.id

export const selectVenueDiscount = (state,venueId)  => get(state,`vendor.menu.discounts[${venueId}]`,[])

